import { fillArr } from '@hglobal/common';

import { FinancialCategory, GetFinancialCategoriesRes } from '../../../models';

const MOCK_FINANCIAL_CATEGORIES: Omit<FinancialCategory, 'id'>[] = [
  {
    name: 'Salary & Calculations',
    description: 'Select this category for inquiries about salary, bonuses, taxes, payroll adjustments, salary reviews, or calculation errors.',
    placeholder: `
<p><strong>Topic:</strong><br>[Salary Calculation / Bonuses / Overtime / Taxes / Maternity Leave / Offboarding / Transfer]</p>
<p><br></p>
<p><strong>Period:</strong><br>[Please specify the month or exact dates related to the issue.]</p>
<p><br></p>
<p><strong>Issue description:</strong><br>[What happened? What amount or result did you expect? What amount or result did you receive?]</p>
<p><br></p>
<p><strong>Supporting documents:</strong><br>[Please attach the payslip for the relevant month, if available. You can find it here: Jira → Staff Card → Fin Info tab → Personal Payroll. If your request is related to overtime, bonus, maternity leave, offboarding, or transfer, please also add a link to the related Jira task/ticket or attach any other supporting documents.]</p>
`.trim(),
  },
  {
    name: 'Payment Processing',
    description: 'Select this category for payment issues: transfers, delays, rejected payments, or updating bank details.',
    placeholder: `
<p>• The standard payout window is 3 days. For B2B payments, it may take an additional 1–5 working days. Please check the exact payout dates in the #Finance Slack channel. Please open a ticket only after the applicable payout period has ended.</p>
<p><br></p>
<p><strong>Topic:</strong><br>[Payout Delay / Missing Payment / Check Payment Status / Blocked Account or Card / Payment Method Update / Wrong Bank Details / Cryptocurrency Issue]</p>
<p><br></p>
<p><strong>Issue description:</strong><br>[What happened? What help do you need?]</p>
<p><br></p>
<p><strong>Payment details:</strong><br>[Please specify the payout date and expected amount.]</p>
<p><br></p>
<p><strong>Payment Method Update (if applicable):</strong><br>[Please specify your current payment method and the new payment method. If changing card details, please provide the full card number required for payment processing.]</p>
<p><br></p>
<p><strong>Bank details update, B2B only:</strong><br>[Please provide your full bank details in text format.]</p>
<p><br></p>
<p><strong>Missing card payment, card payments only:</strong><br>[Please attach your bank account statement for the last 5-7 days.]</p>
`.trim(),
  },
  {
    name: 'Documents & Payslips',
    description: 'Select this category to request payslips, certificates of income, invoices, contracts, or document corrections.',
    placeholder: `
<p><strong>Topic:</strong><br>[Income Certificate / Contract Copy / Payslip / Payment Confirmation / Other]</p>
<p><br></p>
<p><strong>Request Details:</strong><br>[Please describe what document you need or what assistance you require.]</p>
<p><br></p>
<p><strong>Period:</strong><br>[Please specify the month, date range, or payment related to your request.]</p>
<p><br></p>
<p><strong>Purpose (if applicable):</strong><br>[Please specify why you need the document.]</p>
<p><br></p>
<p><strong>Required By (if applicable):</strong><br>[Please specify the date by which you need the document.]</p>
`.trim(),
  },
  {
    name: 'System Issues',
    description: 'Select this category if you experienced a technical issue with a finance-related system.',
    placeholder: `
<p><strong>System:</strong><br>[Please specify the system where the issue occurred.]</p>
<p><br></p>
<p><strong>Issue Description:</strong><br>[Please describe the issue and any error messages you received.]</p>
<p><br></p>
<p><strong>When did the issue start?</strong><br>[Please provide the date and approximate time when the issue first occurred.]</p>
<p><br></p>
<p><strong>Steps Taken (if applicable):</strong><br>[Please describe what action you were trying to perform when the issue occurred.]</p>
<p><br></p>
<p><strong>Screenshot (Required):</strong><br>[Please attach a full screenshot showing the issue or error message.]</p>
`.trim(),
  },
  {
    name: 'Other',
    description: 'Select this category only if none of the above options match your request.',
    placeholder: `
<p><strong>Request Details:</strong><br>[Please describe your question or issue and the assistance you need.]</p>
<p><br></p>
<p><strong>Related Information (if applicable):</strong><br>[Please attach any relevant documents, screenshots, links, or related Jira tickets.]</p>
`.trim(),
  },
];

export function getFinancialCategoriesMock(count = 5): GetFinancialCategoriesRes {
  return {
    categories: fillArr(count, (i: number): FinancialCategory => ({
      id: i,
      ...MOCK_FINANCIAL_CATEGORIES[i % MOCK_FINANCIAL_CATEGORIES.length],
    })),
  };
}
